/**
 * Issue #212 — deciding where the grounds map should look.
 *
 * The defect this exists to fix: `GroundsMap.jsx` mounted its map with a
 * hard-coded `center={[20, 30]} zoom={2}` and nothing ever moved it, so
 * searching "Thornmere Park" narrowed the markers to one speck on a world map
 * and the view stayed put. Searching *looked* broken because the only
 * surface the eye is on — the map — never acknowledged the search.
 *
 * The decision is separated from the rendering because the interesting
 * part is arithmetic (which results can be shown, one point or a box,
 * what happens when a box has no area) and this repo mounts no components
 * in unit tests. Keeping it here makes it testable; see `mapFit.test.js`.
 */

import { haversineKm } from "./distance";
import { NEAR_ME_RADIUS_KM } from "./nearMe";

/**
 * A single result gets a town-scale view, deliberately NOT leaflet's
 * maximum. The issue is explicit about this: "one result → centre and zoom
 * to a sensible level (not maximum — context matters)". At z13 an Auckland
 * ground is shown with Auckland around it, which is what tells the user the
 * map moved to the right place. The ground-detail dialog already goes to
 * z17 on satellite tiles when the user asks for that one ground.
 */
export const SINGLE_RESULT_ZOOM = 13;

/**
 * Ceiling for a multi-result fit. Without it, two grounds 200m apart fit to
 * a box so small leaflet picks its max zoom and the user loses all context
 * — the same "where am I?" failure as the world view, at the other end.
 */
export const MAX_FIT_ZOOM = 15;

/**
 * Below this span (degrees, either axis) a bounding box is treated as a
 * single point. Covers the genuinely-degenerate case (every match at
 * identical coordinates, e.g. duplicate rows for one venue) and the
 * effectively-degenerate one, where `fitBounds` on a sliver box produces a
 * jarring maximum-zoom jump.
 */
const DEGENERATE_SPAN_DEG = 0.0005; // ~55m of latitude

function toFiniteNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Does this ground have coordinates the map can actually place?
 *
 * One predicate, used for BOTH the markers and the fitting. They must not
 * drift apart: fitting to a ground that isn't rendered would move the map to
 * an empty spot, which is a worse bug than the one being fixed.
 *
 * Note this is stricter *and* looser than the `g.latitude && g.longitude`
 * test it replaces — stricter because a non-numeric or out-of-range value is
 * rejected rather than passed to leaflet, looser because a genuine 0 (the
 * prime meridian runs through England; the equator through Kenya and
 * Indonesia) is a real coordinate and used to be silently dropped.
 */
export function hasCoords(g) {
  const lat = toFiniteNumber(g?.latitude);
  const lng = toFiniteNumber(g?.longitude);
  return lat !== null && lng !== null && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

/** `[lat, lng]` as numbers, for a ground that has already passed `hasCoords`. */
export function coordsOf(g) {
  return [toFiniteNumber(g.latitude), toFiniteNumber(g.longitude)];
}

// Review round 2, R3 non-blocking (g) — `GroundsMap.jsx:2351`'s own
// `MapContainer center={[20, 30]}` is the world view `computeMapFit`
// (`lib/mapFit.js`) leaves on screen whenever there is nothing to fit to
// (`kind: "none"`: "caller leaves the view alone") — checked directly, not
// assumed, and it is genuinely the shipped default, not a placeholder this
// file alone carried. Neither `GroundPicker.jsx` nor `GroundSearchDialog.jsx`
// renders a map at all, so there is no existing NZ-centred constant
// anywhere in this codebase to reuse; `[20, 30]` (mid-Atlantic-adjacent,
// off the coast of West Africa) was simply copied from the shipped map's
// own fallback without checking what it actually showed a New Zealand
// player. Replaced here with a real NZ-wide default — central New
// Zealand, zoomed out enough to show most of both islands — rather than left matching a fallback that was itself
// wrong for this product's users.
export const NZ_FALLBACK_CENTER = [-41.2, 174.8];
// #1193 re-check at the taller band: at zoom 5 one pixel is about 3.7 km at
// this latitude, so NZ (about 1,430 km north to south) is about 390px tall.
// The band is 40vh clamped to 220-360px: 338px at 390x844, so nearly the
// whole country; 267px on a 667px phone, so the North Island and the top of
// the South. At the old 118px it showed about 435 km, so the "most of both
// islands" claim above was only ever true of the taller box. Zoom 4 would
// fit all of NZ at 220px but is too coarse to pick a ground from, so 5 stays.
export const NZ_FALLBACK_ZOOM = 5;

/**
 * #1619 (D439) — the copy of `lng` nearest `anchorLng` on the wrapped world.
 *
 * The new-look maps wrap, so a longitude is one of infinitely many copies
 * (`lng + 360k`). Returns the one within 180 degrees of `anchorLng`, which is
 * how a Chathams pin (-176.5) lands just east of New Zealand (183.5) rather
 * than a world away to the west.
 */
export function wrapLngNear(lng, anchorLng) {
  return lng + 360 * Math.round((anchorLng - lng) / 360);
}

/**
 * #1619 — a `[lat, lng]` drawn at the copy of its longitude nearest `anchorLng`.
 * The anchor is the LIVE centre of the view: the wrapping map keeps its centre
 * within -180..180, so a pin placed near anything else (the fit, a constant)
 * leaves the screen the moment a pan crosses the date line.
 */
export function placeLatLng(latlng, anchorLng) {
  return [latlng[0], wrapLngNear(latlng[1], anchorLng)];
}

/** Count-bubble cells with their longitudes moved to the copy nearest `anchorLng`. */
export function placeCells(cells, anchorLng) {
  return cells ? cells.map((c) => ({ ...c, longitude: wrapLngNear(c.longitude, anchorLng) })) : cells;
}

/**
 * #1619 (D439) — what the new-look directory map changes on its `MapContainer`: it opens on New Zealand, wraps
 * (`worldCopyJump` keeps the view and the pins on one copy of the world), and drops the #687 clamp. Spread LAST over
 * the shipped props, so the old look's props stay exactly as they were.
 */
export function newLookMapProps() {
  return {
    center: NZ_FALLBACK_CENTER,
    zoom: NZ_FALLBACK_ZOOM,
    maxBounds: undefined,
    maxBoundsViscosity: undefined,
    worldCopyJump: true,
    // #1620 (D439): no +/− box; the map control pill replaces it (pinch, double-tap, wheel and +/− keys still zoom).
    zoomControl: false,
  };
}

/**
 * #1619 — the longitude pins and count bubbles are drawn near: the fit's own
 * centre, or `fallbackLng` (the mount centre) when there is no fit.
 */
export function fitAnchorLng(fit, fallbackLng) {
  if (fit?.kind === "point") return fit.center[1];
  if (fit?.kind === "bounds") return (fit.bounds[0][1] + fit.bounds[1][1]) / 2;
  return fallbackLng;
}

/** The longitudes, shifted east by 360 where that makes the box narrower, else as given. */
function shortWayRound(lngs) {
  const span = (l) => Math.max(...l) - Math.min(...l);
  if (span(lngs) <= 180) return lngs;
  const shifted = lngs.map((l) => (l < 0 ? l + 360 : l));
  return span(shifted) < span(lngs) ? shifted : lngs;
}

/**
 * Where should the map look, given the current result set?
 *
 * Returns one of:
 *   { kind: "none",   pointCount: 0 }                    — nothing to look at; caller leaves the view alone
 *   { kind: "point",  center: [lat, lng], zoom, pointCount }
 *   { kind: "bounds", bounds: [[s, w], [n, e]], maxZoom, pointCount }
 *
 * `pointCount` is the number of results that could be placed — which is not
 * the number of results (see `hasCoords`), and the caller needs both to say
 * something honest on the map surface.
 *
 * `wrap` (#1619, D439) is the redrawn maps, which wrap the world: a set that
 * is nearer the other way round the date line is fitted there (`east` may then
 * exceed 180). Off (the default, every old-look map) the output is exactly
 * what it was before #1619: those maps draw pins at their stored longitude
 * under `noWrap`, so a shifted box would fit a copy of the world they never show.
 */
export function computeMapFit(results, { wrap = false } = {}) {
  const points = (results || []).filter(hasCoords).map(coordsOf);

  if (points.length === 0) return { kind: "none", pointCount: 0 };

  const lats = points.map((p) => p[0]);
  let lngs = points.map((p) => p[1]);
  // #1619 (wrap only) — a plain min/max across the date line spans nearly the
  // whole world (NZ at 174 plus the Chathams at -176.5 is ~350 degrees). When
  // shifting the western longitudes east by 360 gives a NARROWER box, that is
  // the short way round; otherwise (LA, London, Delhi: 195 plain, 283 shifted)
  // the plain box stays.
  if (wrap) lngs = shortWayRound(lngs);
  const south = Math.min(...lats);
  const north = Math.max(...lats);
  const west = Math.min(...lngs);
  const east = Math.max(...lngs);

  const degenerate = north - south < DEGENERATE_SPAN_DEG && east - west < DEGENERATE_SPAN_DEG;

  if (points.length === 1 || degenerate) {
    return {
      kind: "point",
      center: [(south + north) / 2, (west + east) / 2],
      zoom: SINGLE_RESULT_ZOOM,
      pointCount: points.length,
    };
  }

  return {
    kind: "bounds",
    bounds: [
      [south, west],
      [north, east],
    ],
    maxZoom: MAX_FIT_ZOOM,
    pointCount: points.length,
  };
}

/**
 * #1609 — after "Use my location", the map is centred ON the player.
 *
 * `computeMapFit` over the position plus every ground puts the position
 * wherever it falls inside that bounding box, often at an edge. Here the box
 * is built around the position instead: `dLat`/`dLng` are the largest offsets
 * to any ground within `NEAR_ME_RADIUS_KM`, so the box is symmetric and the
 * position is its exact centre. A ground beyond the radius cannot widen it.
 * With none nearby the fit is a point on the position.
 *
 * `position` is `{ lat, lng }`. The caller (`GroundsMap.jsx`) uses this only
 * while near-me is ready; every other state keeps `computeMapFit`.
 */
export function computeCentredFit(position, results, { wrap = false } = {}) {
  const { lat, lng } = position;
  const near = (results || [])
    .filter(hasCoords)
    .map(coordsOf)
    .filter(([gLat, gLng]) => {
      const km = haversineKm(position, { lat: gLat, lng: gLng });
      return km !== null && km <= NEAR_ME_RADIUS_KM;
    });

  if (near.length === 0) {
    return { kind: "point", center: [lat, lng], zoom: SINGLE_RESULT_ZOOM, pointCount: 1 };
  }

  const dLat = Math.max(...near.map(([gLat]) => Math.abs(gLat - lat)));
  const dLng = Math.max(...near.map(([, gLng]) => Math.abs((wrap ? wrapLngNear(gLng, lng) : gLng) - lng)));

  return {
    kind: "bounds",
    bounds: [
      [lat - dLat, lng - dLng],
      [lat + dLat, lng + dLng],
    ],
    maxZoom: MAX_FIT_ZOOM,
    pointCount: near.length,
  };
}

/**
 * A value that changes exactly when the map should be re-fitted.
 *
 * Identity is useless here: `filtered` is rebuilt on any state change that
 * touches the directory (saving a ground, career stats arriving), and
 * re-fitting on every one of those would yank the map back from wherever the
 * user has panned to. Comparing the *fit* itself — rounded, so float noise
 * from re-parsed coordinates can't trigger a jump — means a pan survives
 * anything that doesn't change which grounds are on screen.
 */
export function fitSignature(fit) {
  if (!fit || fit.kind === "none") return "none";
  const r = (n) => n.toFixed(5);
  if (fit.kind === "point") return `point:${r(fit.center[0])},${r(fit.center[1])}@${fit.zoom}`;
  const [[s, w], [n, e]] = fit.bounds;
  return `bounds:${r(s)},${r(w)},${r(n)},${r(e)}`;
}

/**
 * Does this user want motion? Issue #212 point 4.
 *
 * Guarded for environments without `matchMedia` (jsdom, SSR) — and those
 * default to NOT animating, because a missing preference signal is not
 * consent to move the screen.
 */
export function prefersReducedMotion() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return true;
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return true;
  }
}

/**
 * Apply a fit to a leaflet map instance.
 *
 * Kept next to the arithmetic so the reduced-motion rule lives in one place:
 * with motion suppressed we jump (`setView`/`fitBounds`, `animate: false`),
 * otherwise we glide (`flyTo`/`flyToBounds`). The destination is identical
 * either way — reduced motion removes the animation, never the recentring.
 */
export function applyFit(map, fit, { reducedMotion = prefersReducedMotion() } = {}) {
  if (!map || !fit || fit.kind === "none") return false;

  // #1700 N2: a fit may reserve a corner of its own (the Dashboard preview keeps its chip clear); without one, 40px all round.
  const padding = fit.paddingTopLeft || fit.paddingBottomRight
    ? { paddingTopLeft: fit.paddingTopLeft || [0, 0], paddingBottomRight: fit.paddingBottomRight || [0, 0] }
    : { padding: [40, 40] };

  if (fit.kind === "point") {
    if (reducedMotion) map.setView(fit.center, fit.zoom, { animate: false });
    else map.flyTo(fit.center, fit.zoom, { duration: 0.9 });
    return true;
  }

  if (reducedMotion) map.fitBounds(fit.bounds, { ...padding, maxZoom: fit.maxZoom, animate: false });
  else map.flyToBounds(fit.bounds, { ...padding, maxZoom: fit.maxZoom, duration: 0.9 });
  return true;
}

/**
 * The on-map controls a bubble must be kept clear of, as DOM selectors:
 * leaflet's zoom box, the basemap switcher, the attribution strip, and (#1193,
 * D236) the full-screen button. `GroundsMap.jsx` reads each one's live rect
 * and hands the set to `pushClearOfControls`.
 */
export const MAP_CONTROL_SELECTORS = [
  ".leaflet-control-zoom",
  ".leaflet-control-layers",
  ".leaflet-control-attribution",
  ".nw-map-fs-control",
  // #1620: the new-look maps' one control (locate, map/satellite, full screen).
  ".nw-map-pill",
];

/**
 * PR #858 review round 3 (B3) — pushes a screen-pixel point `pt` (a cell
 * count bubble's own centre, radius `r`) clear of every rect in `rects`
 * (leaflet's own controls — the zoom box, the basemap switcher, the
 * attribution strip — read live off the DOM by the caller), then clamps
 * the result inside `mapSize`. Lives here, not in `GroundsMap.jsx`,
 * because it is pure geometry with no leaflet instance dependency — this
 * repo mounts no components in unit tests (`GroundsMap.test.js`'s own
 * header), so a function worth testing directly has to live somewhere
 * `react-leaflet` itself is not on the import chain.
 *
 * THE BUG THIS FIXES. Leaflet's own zoom control sits flush against the
 * map's top-left corner; `Basemaps`' switcher sits flush against the
 * bottom-right. Pushing a bubble clear of one of them — "go further
 * left/up" for the zoom control — can therefore push it PAST the map's
 * own edge. An earlier version of this function ran the rect-avoidance
 * loop to convergence FIRST and clamped the result inside the map ONCE,
 * afterwards, with no check that the clamp had landed the point right
 * back inside the control it had just escaped — deterministic, not
 * flaky, given the same cell coordinates and the same control sizes on
 * every run, which is why it only ever showed up on ONE runner (a
 * marginal case sensitive to the controls' exact rendered pixel size)
 * rather than intermittently everywhere.
 *
 * The fix: clamp AND re-check rects together, in the SAME loop, and pick
 * — among all four possible escape edges, not just the nearest — the
 * first one that SURVIVES being clamped back inside the map.
 */
export function pushClearOfControls(pt, r, rects, mapSize, pad = 6) {
  let p = pt;
  let everMoved = false;
  for (let pass = 0; pass < 20; pass += 1) {
    let moved = false;
    for (const rect of rects) {
      const minX = rect.x - pad - r;
      const maxX = rect.x + rect.width + pad + r;
      const minY = rect.y - pad - r;
      const maxY = rect.y + rect.height + pad + r;
      if (p.x <= minX || p.x >= maxX || p.y <= minY || p.y >= maxY) continue;
      moved = true;
      // Four candidate escapes (one per edge), NEAREST first — but the
      // nearest one is not always FEASIBLE: an edge-anchored control can
      // have its "escape left" candidate fall OFF the map entirely, which
      // the boundary clamp below would then pull straight back inside
      // this same rect. Try each candidate against the map's bounds and
      // take the first one that is STILL clear once clamped; only fall
      // back to the plain nearest edge if none of the four genuinely fit
      // (truly no room, best effort).
      const candidates = [
        { x: minX, y: p.y, dist: p.x - minX },
        { x: maxX, y: p.y, dist: maxX - p.x },
        { x: p.x, y: minY, dist: p.y - minY },
        { x: p.x, y: maxY, dist: maxY - p.y },
      ].sort((a, b) => a.dist - b.dist);
      let chosen = candidates[0];
      if (mapSize) {
        for (const c of candidates) {
          const cx = Math.min(Math.max(c.x, r + 2), Math.max(r + 2, mapSize.x - r - 2));
          const cy = Math.min(Math.max(c.y, r + 2), Math.max(r + 2, mapSize.y - r - 2));
          const stillInsideThisRect = cx > minX && cx < maxX && cy > minY && cy < maxY;
          if (!stillInsideThisRect) {
            chosen = { x: cx, y: cy };
            break;
          }
        }
      }
      p = { x: chosen.x, y: chosen.y };
    }
    if (mapSize) {
      const clamped = {
        x: Math.min(Math.max(p.x, r + 2), Math.max(r + 2, mapSize.x - r - 2)),
        y: Math.min(Math.max(p.y, r + 2), Math.max(r + 2, mapSize.y - r - 2)),
      };
      if (clamped.x !== p.x || clamped.y !== p.y) {
        moved = true;
        p = clamped;
      }
    }
    if (moved) everMoved = true;
    else break;
  }
  return { pt: p, moved: everMoved };
}

/**
 * #1193 (F3) — the on-map controls' rects, relative to the map container, read
 * live off the DOM. Leaflet's own controls are inside `container`; the
 * full-screen button is its SIBLING in the same `[data-map-area]`, so the
 * search scope is the area when there is one, not the container alone.
 */
export function readControlRects(container) {
  if (!container) return [];
  const scope = container.closest("[data-map-area]") || container;
  const base = container.getBoundingClientRect();
  return MAP_CONTROL_SELECTORS
    .map((sel) => scope.querySelector(sel))
    .filter(Boolean)
    .map((el) => {
      const r = el.getBoundingClientRect();
      return { x: r.left - base.left, y: r.top - base.top, width: r.width, height: r.height };
    });
}
